import { useCallback, useEffect, useState } from 'react'

import { useLocalStorage } from '@buffer-mono/popcorn'

// Threshold for showing the "turn reply into post" coachmark
const LONG_REPLY_THRESHOLD = 250

// LocalStorage key
const COACHMARK_DISMISSED_KEY = 'turn-reply-into-post-coachmark-dismissed'

type UseTurnReplyIntoPostCoachmarkReturn = {
  shouldShowCoachmark: boolean
  onDismissCoachmark: () => void
}

/**
 * Hook for managing the "turn reply into post" coachmark.
 *
 * Shows a coachmark when the user types a reply with 250+ characters,
 * suggesting they could turn their long reply into a post.
 *
 * Behavior:
 * - Coachmark appears when reply text reaches 250+ characters
 * - Once shown, it "latches" and stays visible even if text drops below 250 (within the session)
 * - Only disappears when explicitly dismissed
 * - Once dismissed (via localStorage), never shows again
 *
 * @param replyTextLength - Current length of the reply text
 * @returns Object containing:
 * - `shouldShowCoachmark`: Whether to show the coachmark
 * - `onDismissCoachmark`: Call when user dismisses the coachmark
 *
 * @example
 * ```tsx
 * const { shouldShowCoachmark, onDismissCoachmark } = useTurnReplyIntoPostCoachmark(replyText.length)
 *
 * {shouldShowCoachmark && (
 *   <TurnReplyIntoPostCoachmark onDismiss={onDismissCoachmark} />
 * )}
 * ```
 */
export const useTurnReplyIntoPostCoachmark = (
  replyTextLength: number,
): UseTurnReplyIntoPostCoachmarkReturn => {
  const [hasBeenDismissed, setHasBeenDismissed] = useLocalStorage<boolean>(
    COACHMARK_DISMISSED_KEY,
    false,
  )

  // Latching behavior: once the coachmark is shown, keep it visible until dismissed (within session)
  const [hasBeenShown, setHasBeenShown] = useState(false)

  const isLongReply = replyTextLength >= LONG_REPLY_THRESHOLD

  // Latch the coachmark visibility when threshold is reached
  useEffect(() => {
    if (isLongReply && !hasBeenDismissed) {
      setHasBeenShown(true)
    }
  }, [isLongReply, hasBeenDismissed])

  const shouldShowCoachmark = hasBeenShown && !hasBeenDismissed

  const onDismissCoachmark = useCallback(() => {
    setHasBeenDismissed(true)
    setHasBeenShown(false)
  }, [setHasBeenDismissed])

  return {
    shouldShowCoachmark,
    onDismissCoachmark,
  }
}
